import {
    Box,
    Checkbox,
    Container,
    FormControlLabel,
    InputAdornment,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useRegisterMainUserMutation } from '../../api/authApiSlice';
import logo from '../../assets/church.svg';
import FormikButton from '../../components/formik/FormikButton';
import FormikInput from '../../components/formik/FormikInput';
import FormikSelect from '../../components/formik/FormikSelect';
import Form from '../../components/utilities/Form';
import { gender2Inputs, salvationInputs } from '../../enums';
import errorSnackbar from '../../utils/errorSnackbar';

const validate = Yup.object().shape({
    firstName: Yup.string().required().label('First Name'),
    surname: Yup.string().required().label('Surname'),
    gender: Yup.string().required().label('Gender'),
    phoneNumber: Yup.string().required().min(9).max(10).label('Phone Number'),
    email: Yup.string().email().required().label('Email Address'),
    salvation: Yup.string().required().label('Salvation Status'),
    password: Yup.string().required().min(6).label('Password'),
    password_confirmation: Yup.string().oneOf(
        [Yup.ref('password'), null],
        'Passwords must match'
    ),
});

export default function RegisterScreen() {
    const [accept, setAccept] = useState(false);

    const { state: church } = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [registerMainUser, { isLoading }] = useRegisterMainUserMutation();

    useEffect(() => {
        !church && navigate('/churches', { replace: true });
    }, [church, navigate]);

    const onSubmit = async (vals) => {
        const { error, data } = await registerMainUser({
            ...vals,
            church: church?.id,
            phoneNumber: '0' + vals.phoneNumber,
        });

        errorSnackbar(error, enqueueSnackbar);

        if (data) {
            enqueueSnackbar(
                'Registration Successful. Wait for approval message in your email inbox before you can login.'
            );
            navigate('/guest-profile');
        }
    };

    return (
        <Container sx={{ py: 2 }} maxWidth="sm">
            <Form
                onSubmit={(vals) => onSubmit(vals)}
                validationSchema={validate}
                initialValues={{
                    firstName: '',
                    surname: '',
                    gender: '',
                    phoneNumber: '',
                    email: '',
                    salvation: '',
                    password: '',
                    password_confirmation: '',
                }}
            >
                <img style={{ width: 30, height: 30 }} src={logo} alt="logo" />
                <Box sx={{ mt: 6, mb: 4 }}>
                    <Typography variant="h6">
                        Get started on myKanisa
                    </Typography>
                    <Typography>
                        Have an account?{' '}
                        <Typography
                            onClick={() => navigate('/guest-profile')}
                            className="fw-bold text-underline"
                            color="primary"
                            component="span"
                        >
                            Sign in
                        </Typography>
                    </Typography>
                </Box>
                <FormikInput name="firstName" label="First Name" />
                <FormikInput name="surname" label="Surname" />
                <FormikInput
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                +254
                            </InputAdornment>
                        ),
                    }}
                    name="phoneNumber"
                    label="Phone Number"
                    placeholder="7XXXXXXXX"
                />
                <FormikInput name="email" label="Email Address" />
                <FormikSelect
                    name="gender"
                    label="Gender"
                    data={gender2Inputs}
                />
                <FormikSelect
                    name="salvation"
                    label="Salvation Status"
                    data={salvationInputs}
                />
                <FormikInput type="password" name="password" label="Password" />
                <FormikInput
                    type="password"
                    name="password_confirmation"
                    label="Confirm Password"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={accept}
                            onChange={() => setAccept(!accept)}
                        />
                    }
                    label="I agree to the terms and conditions"
                />

                <div className="d-flex align-items-center justify-content-between">
                    <FormikButton
                        disabled={!accept}
                        loading={isLoading}
                        variant="contained"
                    >
                        Register
                    </FormikButton>
                </div>
            </Form>
        </Container>
    );
}
